import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, Save } from 'lucide-react';
import * as Icons from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const RatingCertificatePledgePage = () => {
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [pillars, setPillars] = useState([]);
    const [settings, setSettings] = useState({
        section_tag: '',
        section_title: '',
        section_intro: '',
        footer_text: ''
    });

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPillar, setEditingPillar] = useState(null);
    const [modalData, setModalData] = useState({
        title: '',
        description: '',
        icon_name: 'Award',
        display_order: 0,
        is_active: true
    });

    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
    const loanType = 'rating_certificate_pledge';

    // Available Icons for selection (using Lucide icons as per other admin pages)
    const iconOptions = [
        'Award',
        'TrendingUp',
        'BarChart3',
        'Shield',
        'CheckCircle',
        'Star',
        'Target',
        'Users'
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, pillarsRes] = await Promise.all([
                axios.get('/api/admin/rating-pledge-settings'),
                axios.get(`/api/admin/key-benefits/${loanType}`)
            ]);

            if (settingsRes.data.success) {
                setSettings(settingsRes.data.data || {
                    section_tag: '',
                    section_title: '',
                    section_intro: '',
                    footer_text: ''
                });
            }

            if (pillarsRes.data.success) {
                setPillars(pillarsRes.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Settings Handlers
    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        setSavingSettings(true);
        try {
            await axios.put('/api/admin/rating-pledge-settings', settings);
            toast.success('Section settings saved');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    // Pillar Handlers
    const handleAddNew = () => {
        setEditingPillar(null);
        setModalData({
            title: '',
            description: '',
            icon_name: 'Award',
            display_order: pillars.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleEdit = (pillar) => {
        setEditingPillar(pillar);
        setModalData({
            title: pillar.title,
            description: pillar.description || '',
            icon_name: pillar.icon_name,
            display_order: pillar.display_order,
            is_active: pillar.is_active
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/key-benefits/${deleteConfirm.id}`);
            toast.success('Pillar deleted successfully');
            fetchData();
        } catch (error) {
            console.error('Error deleting pillar:', error);
            toast.error('Failed to delete pillar');
        } finally {
            setDeleteConfirm({ isOpen: false, id: null });
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...modalData, loan_type: loanType };

            if (editingPillar) {
                await axios.put(`/api/admin/key-benefits/${editingPillar.id}`, payload);
                toast.success('Pillar updated successfully');
            } else {
                await axios.post('/api/admin/key-benefits', payload);
                toast.success('Pillar added successfully');
            }

            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving pillar:', error);
            toast.error('Failed to save pillar');
        }
    };

    // Render Icon Helper
    const renderIcon = (iconName, className = "h-6 w-6") => {
        const IconComponent = Icons[iconName] || Icons.Star;
        return <IconComponent className={className} />;
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Our Pledge</h1>
                    <p className="text-slate-500">Manage commitment section</p>
                </div>
            </div>

            {/* Settings Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h2 className="text-lg font-bold text-slate-800 mb-4">Section Settings</h2>
                <form onSubmit={saveSettings} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Tag</label>
                        <input
                            type="text"
                            name="section_tag"
                            value={settings.section_tag}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Our Pledge"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={settings.section_title}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Our Promise..."
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Intro Text</label>
                        <textarea
                            name="section_intro"
                            value={settings.section_intro}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            rows={3}
                            placeholder="Intro text..."
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Footer Text</label>
                        <textarea
                            name="footer_text"
                            value={settings.footer_text}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            rows={2}
                            placeholder="Footer text..."
                        />
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={savingSettings}
                            className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700 transition-colors"
                        >
                            <Save size={18} />
                            {savingSettings ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Commitment Pillars List */}
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-lg font-bold text-slate-800">Commitment Pillars</h2>
                    <button
                        onClick={handleAddNew}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors"
                    >
                        <Plus size={18} /> Add New
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {pillars.map((pillar) => (
                        <div key={pillar.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center mb-4">
                                {renderIcon(pillar.icon_name)}
                            </div>
                            <h3 className="font-bold text-slate-800 mb-2">{pillar.title}</h3>
                            <p className="text-sm text-slate-500 mb-4 h-10 overflow-hidden">{pillar.description}</p>
                            <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                                <span className={`text-xs font-medium px-2 py-1 rounded-lg ${pillar.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                    {pillar.is_active ? 'Active' : 'Inactive'}
                                </span>
                                <div className="flex gap-2">
                                    <button onClick={() => handleEdit(pillar)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                                        <Pencil size={16} />
                                    </button>
                                    <button onClick={() => handleDelete(pillar.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Edit/Add Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                        <h2 className="text-xl font-bold mb-6">{editingPillar ? 'Edit Pillar' : 'Add New Pillar'}</h2>
                        <form onSubmit={handleModalSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Title</label>
                                <input
                                    type="text"
                                    value={modalData.title}
                                    onChange={(e) => setModalData({ ...modalData, title: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-xl"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Description</label>
                                <textarea
                                    value={modalData.description}
                                    onChange={(e) => setModalData({ ...modalData, description: e.target.value })}
                                    className="w-full px-4 py-2 border rounded-xl"
                                    rows={3}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Icon</label>
                                    <select
                                        value={modalData.icon_name}
                                        onChange={(e) => setModalData({ ...modalData, icon_name: e.target.value })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                    >
                                        {iconOptions.map(icon => (
                                            <option key={icon} value={icon}>{icon}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={modalData.display_order}
                                        onChange={(e) => setModalData({ ...modalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border rounded-xl"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center h-full pt-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={modalData.is_active}
                                        onChange={(e) => setModalData({ ...modalData, is_active: e.target.checked })}
                                        className="w-5 h-5 rounded text-primary-600"
                                    />
                                    <span className="font-medium">Active</span>
                                </label>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2 border rounded-xl hover:bg-slate-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700"
                                >
                                    Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Pillar"
                message="Are you sure you want to delete this commitment pillar?"
            />
        </div>
    );
};

export default RatingCertificatePledgePage;
